import type { ReportView, Site, ViewBlock, ViewSection } from '@/data/schemas';
import { parseKeypath } from '@/data/idGenerator';
import { extractUnitIndex } from '@/features/reports/utils/navigationHelpers';
import { formatUnitDisplayLabel } from '@/utils/entityIdentity';
import { parseFieldSource, type SupportedEntity } from '@/features/reports/utils/fieldSourceParser';
import type { FieldSourceMeta } from '../types';

export function resolveSectionPresentation(
  section: ViewSection,
  site: Site | null
): { resolvedTitle: string; anchor?: string } {
  const unitIndex = extractUnitIndex(section.id);
  const unitRecord = unitIndex !== null ? site?.refrigerationUnits?.[unitIndex] : undefined;
  const unitLabel = formatUnitDisplayLabel(unitRecord?.name ?? null, unitIndex ?? undefined);

  const resolvedTitle = section.title
    .replace(/\{\{\s*unit\.name\s*\}\}/g, unitLabel)
    .replace(/\{\{\s*unitCheck\.unitName\s*\}\}/g, unitLabel);

  return { resolvedTitle, anchor: section.id };
}

export function collectFieldSourceMeta(view: ReportView): Record<string, FieldSourceMeta> {
  const meta: Record<string, FieldSourceMeta> = {};

  const registerField = (
    item:
      | Extract<ViewBlock, { type: 'kv' }>['items'][number]
      | Extract<ViewBlock, { type: 'row-group' }>['rows'][number]['items'][number]
      | Extract<ViewBlock, { type: 'checklist' }>['items'][number]
  ) => {
    if (!('keypath' in item) || !('fieldSource' in item)) {
      return;
    }
    const keypath = item.keypath;
    const source = item.fieldSource;
    if (!keypath || !source) return;
    const parsedSource = parseFieldSource(source);
    if (!parsedSource) return;
    const parsedKeypath = parseKeypath(keypath);
    meta[keypath] = {
      source: parsedSource.raw,
      entity: parsedSource.entity as SupportedEntity,
      fieldType: 'fieldType' in item && item.fieldType ? item.fieldType : undefined,
      indices: parsedKeypath.indices,
    };
  };

  const walkBlock = (block: ViewBlock) => {
    switch (block.type) {
      case 'kv': {
        block.items.forEach(registerField);
        break;
      }
      case 'row-group': {
        block.rows.forEach((row) => {
          row.items.forEach(registerField);
        });
        break;
      }
      case 'checklist': {
        block.items.forEach(registerField);
        break;
      }
      case 'grid': {
        block.columns.forEach((column) => {
          column.blocks.forEach(walkBlock);
        });
        break;
      }
      default:
        break;
    }
  };

  view.sections.forEach((section) => {
    (section.blocks as ViewBlock[]).forEach((block) => walkBlock(block));
  });

  return meta;
}
